/**
 * Moving a quest on the Quest Studio board (#577 rows 5 and 6) — the rules, with no React and no
 * network, so every rule can be tested on its own.
 *
 * A move is one of two things, and each goes to the endpoint that already owns it:
 *
 * | move | write | what changes |
 * | --- | --- | --- |
 * | up / down, or a drop onto a card the same day | `PUT /api/chore-order` | the holder's list order |
 * | onto another day | `PUT /api/admin/chores/:id` with `nextExecutionTime` | the next due date only |
 *
 * **A reorder is always ONE person's list.** `display_order` is a per-person number: the server
 * refuses a list mixing two people's quests (`server/services/chore.service.js`, "All chores must
 * belong to the same user"), and a rotation appends a quest to the bottom of its new holder's
 * list. So a quest can pass only the same person's quests, and the list sent is that person's
 * whole list in saved display order, with quest id breaking ties, just like the child's screen.
 * A within-day move replaces only that day's slots in the full list; other days keep their places.
 *
 * **A day move keeps the quest's own schedule.** It writes the next due date and nothing else. A
 * day the quest's `scheduleDays` do not allow is refused here, because saving it would put the
 * quest on a day the next rollover then quietly moves it off.
 *
 * Every function returns a NEW board and never changes the one it was given. The route keeps the
 * old one to roll back to, and a plan that edited it in place would leave nothing to roll back.
 */
import { DAY_KEYS, isoWeekday, sortBoard } from './questStudioMapping.js';

// One home for the day keys and the weekday clock: the mapping places a card with them, so a move
// must read them from the same place. Re-exported for callers that already import it from here.
export { isoWeekday };

/** The server's own limit on one reorder (`reorderChoresSchema`, `.max(100)`). */
export const MAX_REORDER = 100;

const DAY_FULL = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

const clone = (board) => ({
  ...board,
  columns: board.columns.map((c) => ({ ...c, tiles: c.tiles.map((t) => ({ ...t })) })),
});

const sameOwner = (a, b) => (a.ownerId ?? null) === (b.ownerId ?? null);

function locate(board, tileId) {
  const columns = board && Array.isArray(board.columns) ? board.columns : [];
  for (let c = 0; c < columns.length; c += 1) {
    const i = columns[c].tiles.findIndex((t) => t.id === tileId);
    if (i >= 0) return { c, i, tile: columns[c].tiles[i] };
  }
  return null;
}

/** "Monday", "Monday and Thursday", "Monday, Wednesday and Friday". */
function dayList(isoDays) {
  const names = [...isoDays].sort((a, b) => a - b).map((d) => DAY_FULL[d - 1]).filter(Boolean);
  if (names.length <= 1) return names.join('');
  return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
}

/**
 * Can this quest move one place up or down inside its day?
 *
 * Only past a neighbour held by the SAME person. Passing someone else's quest would need a list
 * mixing two people, which the server refuses.
 */
export function canMoveWithinDay(board, tileId, direction) {
  const at = locate(board, tileId);
  if (!at) return false;
  const neighbour = board.columns[at.c].tiles[at.i + (direction === 'up' ? -1 : 1)];
  return Boolean(neighbour) && sameOwner(neighbour, at.tile);
}

/**
 * Plan a reorder inside one day.
 *
 * @param {object} board the board as shown
 * @param {*} tileId the quest being moved
 * @param {{direction?: 'up'|'down', targetId?: *}} how one place up or down, or into the place of
 *   the card it was dropped on
 * @returns {null|{refusal: string}|{board: object, orderedIds: Array, tile: object}}
 *   `null` when nothing would change; a `refusal` sentence when the move cannot be saved.
 */
export function planReorder(board, tileId, how = {}) {
  const at = locate(board, tileId);
  if (!at) return null;
  const tiles = board.columns[at.c].tiles;

  let to;
  if (how.targetId !== undefined) {
    to = tiles.findIndex((t) => t.id === how.targetId);
    if (to < 0) return null;
    if (!sameOwner(tiles[to], at.tile)) {
      const who = at.tile.assignee ? `${at.tile.assignee}'s` : 'the unassigned';
      return {
        refusal: `${at.tile.title} can move only among ${who} quests on ${board.columns[at.c].full || 'that day'}.`,
      };
    }
  } else {
    if (!canMoveWithinDay(board, tileId, how.direction)) return null;
    to = at.i + (how.direction === 'up' ? -1 : 1);
  }
  if (to === at.i) return null;

  const next = clone(board);
  const column = next.columns[at.c];
  const [moved] = column.tiles.splice(at.i, 1);
  column.tiles.splice(to, 0, moved);

  // Start with the child's saved order across ALL days. Replace only the selected day's slots
  // with that day's new order, so moving Monday's cards cannot reorder Tuesday's quests.
  const ordered = column.tiles.filter((t) => sameOwner(t, moved));
  const dayIds = new Set(ordered.map((t) => t.id));
  const saved = next.columns.flatMap((c) => c.tiles).filter((t) => sameOwner(t, moved))
    .sort((a, b) => {
      const oa = Number.isInteger(a.displayOrder) ? a.displayOrder : Infinity;
      const ob = Number.isInteger(b.displayOrder) ? b.displayOrder : Infinity;
      return (oa === ob ? 0 : oa < ob ? -1 : 1) || a.id - b.id;
    });
  let slot = 0;
  const mine = saved.map((t) => (dayIds.has(t.id) ? ordered[slot++] : t));
  // A quest of theirs the board could not place is missing from `mine`, and would keep an old
  // number that can collide with the ones this list writes.
  if (Array.isArray(board.unplaced) && board.unplaced.some((u) => sameOwner(u, moved))) {
    return {
      refusal: `${moved.title} could not be moved — the board could not show every quest on that list. `
        + 'Reload the page and try again.',
    };
  }
  if (mine.length > MAX_REORDER) {
    return { refusal: `${moved.title} could not be moved — that list has too many quests to reorder at once.` };
  }
  // The numbers the server will write (`reorderChores` stores each id's index). Mirroring them
  // now means the board after a reload is the board the parent is looking at.
  mine.forEach((t, index) => { t.displayOrder = index; });
  return { board: next, orderedIds: mine.map((t) => t.id), tile: moved };
}

/**
 * The next time `isoDay` comes round, at the quest's own time of day, and never in the past.
 *
 * Counted from NOW rather than from the stored date, because the board shows each quest on the
 * day it next falls: a card dropped on Monday means "the coming Monday".
 *
 * ponytail: local browser time, the same clock `toBoard` uses to place the card. The family's own
 * timezone is not on this screen; a browser in a different zone from the family can land the
 * quest one day off across midnight. Upgrade path: pass the tenant timezone and use Intl.
 */
export function nextDueOn(dueAt, isoDay, now = new Date()) {
  const current = new Date(dueAt);
  const due = new Date(now);
  due.setHours(current.getHours(), current.getMinutes(), current.getSeconds(), 0);
  for (let step = 0; step < 7 && isoWeekday(due) !== isoDay; step += 1) {
    due.setDate(due.getDate() + 1);
  }
  if (due.getTime() < now.getTime()) due.setDate(due.getDate() + 7);
  return due.toISOString();
}

/**
 * Plan a move onto another day.
 *
 * @returns {null|{refusal: string}|{board: object, nextExecutionTime: string, toIsoDay: number,
 *   toDay: string, tile: object}} `null` when the card is already on that day.
 */
export function moveToDay(board, tileId, toColumnId, now = new Date(), kids = []) {
  const at = locate(board, tileId);
  const to = DAY_KEYS.indexOf(toColumnId);
  if (!at || to < 0 || to === at.c) return null;
  const toIsoDay = to + 1;
  const tile = at.tile;

  if (Array.isArray(tile.scheduleDays) && tile.scheduleDays.length > 0 && !tile.scheduleDays.includes(toIsoDay)) {
    return {
      refusal: `${tile.title} happens only on ${dayList(tile.scheduleDays)}. `
        + 'Change its days in the editor first, then move it.',
    };
  }

  const nextExecutionTime = nextDueOn(tile.dueAt, toIsoDay, now);
  const next = clone(board);
  const [moved] = next.columns[at.c].tiles.splice(at.i, 1);
  moved.dueAt = nextExecutionTime;
  next.columns[to].tiles.push(moved);
  // Sorted the same way `toBoard` sorts a fresh load, so the card lands where a reload puts it.
  next.columns = sortBoard(next.columns, kids);
  return { board: next, nextExecutionTime, toIsoDay, toDay: DAY_FULL[to], tile: moved };
}
